import React, { useState } from 'react';
import { subDays } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Clock, Calendar as CalendarIcon } from 'lucide-react';
import { formatDT, resolveTimezone } from '../../utils/timezone';

/**
 * Shared date-range preset filter — the EXACT UX of Analytics > Events >
 * Event Log: a compact Select with All Time / hour / day presets, plus a
 * "Custom Range" option that reveals two date+time pickers.
 *
 * Props:
 *  - value       { from: Date|undefined, to: Date|undefined }
 *  - onChange    fn({ from, to })  — undefined values mean "no bound" (All Time)
 *  - testIdPrefix e.g. "shared-evidence-date"
 *
 * The emitted Dates are TIME-EXACT (e.g. "Last 1 Hour" → from=now-1h, to=now),
 * so consumers must NOT re-normalise `to` to end-of-day.
 */
export const DateRangePresetFilter = ({ value, onChange, testIdPrefix = 'date-preset', resetSignal, triggerClassName = '' }) => {
  const from = value?.from || null;
  const to = value?.to || null;
  const [preset, setPreset] = useState(() => ((value?.from || value?.to) ? 'custom' : 'all'));
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  // Parent bumps resetSignal (e.g. its Refresh button) to snap back to All Time.
  React.useEffect(() => {
    if (resetSignal) setPreset('all');
  }, [resetSignal]);

  const applyPreset = (v) => {
    setPreset(v);
    const now = new Date();
    if (v === 'all') onChange({ from: undefined, to: undefined });
    else if (v === '1hour') onChange({ from: new Date(now.getTime() - 3600000), to: now });
    else if (v === '2hours') onChange({ from: new Date(now.getTime() - 7200000), to: now });
    else if (v === '3hours') onChange({ from: new Date(now.getTime() - 10800000), to: now });
    else if (v === 'today') { const s = new Date(now); s.setHours(0, 0, 0, 0); onChange({ from: s, to: now }); }
    else if (v === 'yesterday') { const s = subDays(now, 1); s.setHours(0, 0, 0, 0); const e = new Date(s); e.setHours(23, 59, 59, 999); onChange({ from: s, to: e }); }
    else if (v === '7days') onChange({ from: subDays(now, 7), to: now });
    else if (v === '30days') onChange({ from: subDays(now, 30), to: now });
    // Custom starts EMPTY (matches Event Log) — user picks both bounds below.
    else if (v === 'custom') onChange({ from: undefined, to: undefined });
  };

  return (
    <>
      <Select value={preset} onValueChange={applyPreset}>
        <SelectTrigger className={`w-36 h-8 ${triggerClassName}`} data-testid={`${testIdPrefix}-filter`}>
          <SelectValue placeholder="Date Range" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Time</SelectItem>
          <SelectItem value="1hour">Last 1 Hour</SelectItem>
          <SelectItem value="2hours">Last 2 Hours</SelectItem>
          <SelectItem value="3hours">Last 3 Hours</SelectItem>
          <SelectItem value="today">Today</SelectItem>
          <SelectItem value="yesterday">Yesterday</SelectItem>
          <SelectItem value="7days">Last 7 Days</SelectItem>
          <SelectItem value="30days">Last 30 Days</SelectItem>
          <SelectItem value="custom">Custom Range</SelectItem>
        </SelectContent>
      </Select>
      {preset === 'custom' && (
        <div className="flex items-center gap-2">
          <Popover open={startOpen} onOpenChange={setStartOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className={`h-8 text-xs font-normal justify-between min-w-[180px] ${triggerClassName}`} data-testid={`${testIdPrefix}-custom-start`}>
                {from ? formatDT(from, { timezone: resolveTimezone(), seconds: false }) : 'Start date'}
                <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-[10000]" align="start">
              <Calendar
                mode="single"
                selected={from || undefined}
                onSelect={(date) => {
                  if (!date) return;
                  date.setHours(from?.getHours() || 0, from?.getMinutes() || 0, 0, 0);
                  onChange({ from: date, to: to || undefined });
                }}
              />
              <div className="border-t p-3 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  type="time"
                  value={from ? `${String(from.getHours()).padStart(2, '0')}:${String(from.getMinutes()).padStart(2, '0')}` : '00:00'}
                  onChange={(e) => {
                    const [h, m] = e.target.value.split(':').map(Number);
                    const d = new Date(from || new Date());
                    d.setHours(h || 0, m || 0, 0, 0);
                    onChange({ from: d, to: to || undefined });
                  }}
                  className="h-8 text-sm flex-1"
                />
                <Button size="sm" className="h-8 text-xs" onClick={() => setStartOpen(false)}>Done</Button>
              </div>
            </PopoverContent>
          </Popover>
          <span className="text-xs text-muted-foreground">to</span>
          <Popover open={endOpen} onOpenChange={setEndOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className={`h-8 text-xs font-normal justify-between min-w-[180px] ${triggerClassName}`} data-testid={`${testIdPrefix}-custom-end`}>
                {to ? formatDT(to, { timezone: resolveTimezone(), seconds: false }) : 'End date'}
                <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-[10000]" align="start">
              <Calendar
                mode="single"
                selected={to || undefined}
                onSelect={(date) => {
                  if (!date) return;
                  date.setHours(to?.getHours() ?? 23, to?.getMinutes() ?? 59, 59, 999);
                  onChange({ from: from || undefined, to: date });
                }}
              />
              <div className="border-t p-3 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  type="time"
                  value={to ? `${String(to.getHours()).padStart(2, '0')}:${String(to.getMinutes()).padStart(2, '0')}` : '23:59'}
                  onChange={(e) => {
                    const [h, m] = e.target.value.split(':').map(Number);
                    const d = new Date(to || new Date());
                    d.setHours(h || 0, m || 0, 59, 999);
                    onChange({ from: from || undefined, to: d });
                  }}
                  className="h-8 text-sm flex-1"
                />
                <Button size="sm" className="h-8 text-xs" onClick={() => setEndOpen(false)}>Done</Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </>
  );
};
